import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStore } from '@/stores/SessionStore'
import { useUserStore } from '@/stores/UserStore'
import { useCachedFetch } from '@/composables/useFetch'
import { useUnitConverter } from '@/composables/useUnitConverter'

import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
import useSettingsGlobals from '@/composables/useSettingsGlobals'
import useSpotGlobals from '@/composables/useSpotGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { $WEB_VIEW, $PLATFORM } = useSettingsGlobals();
const spotGlobals = useSpotGlobals();

export let useSpotStore = defineStore('spotStore', () => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();

  const unitConverter = useUnitConverter();
  
  let spots = ref(null);
  let spot = ref(null);
  let spotId = ref(null);
  let spotFavoriteId = ref(null);
  let spotName = ref(null);
  let spotProducts = ref(null);
  let spotRecordDetails = ref(null)
  let defaultStation = ref(null);
  let forecastModelData = ref(null);
  let nearbyTidesData = ref(null);

  let spotMessage = ref(null);
  
  let iconColor = ref(null);
  let iconType = ref(null);
  
  let lat = ref(null);
  let lon = ref(null);
  
  let currentTimeLocal = ref(null);
  let currentTimeUTC = ref(null);
  let localTimezone = ref(null);
  let localTimezoneShort = ref(null);
  let localUTCOffset = ref(null);
  
  let upgradeAvailable = ref(true);
  let modelUpgradeAvailable = ref(false);

  let ncWindDataEnabled = ref(
    Object.prototype.hasOwnProperty.call(spotGlobals, '$NC_WIND_DATA_ENABLED')
      ? spotGlobals['$NC_WIND_DATA_ENABLED']
      : true
  )

  let lastAPICallFunction = ref(null);

  const isNowcast = computed(() => defaultStation.value.type === 100 || defaultStation.value.type === 101);
  const status = computed(() => defaultStation.value.status_id);
  const sourceMessage = computed(() => defaultStation.value.source_message)
  const statusMessage = computed(() => defaultStation.value.status_message)
  const forecastModels = computed(() => forecastModelData.value.model_summary)
  const hasNearbyTidesOrCurrents = computed(() => nearbyTidesData.value.data_values.length > 0);
  const hasNearbyTides = computed(() => nearbyTidesData.value.data_values.filter(location => location[2] === 10).length > 0);
  const hasNearbyCurrents = computed(() => nearbyTidesData.value.data_values.filter(location => location[2] === 18).length > 0);
  const isNCWind = computed(() => defaultStation.value.is_nc_wind);
  const isNCWindCapable = computed(() => defaultStation.value.is_nc_wind_capable)

  const hasSpotProduct = computed(() => (spotProductId) => spotProducts.value?.includes(spotProductId));

  const isSpotFavorited = computed(() => {
    let spotFavorited = null

    if (!userStore.userProfiles.profiles) return spotFavorited
    if (userStore.userProfiles.profiles.length === 0) return spotFavorited

    for (let profile of userStore.userProfiles.profiles) {
      spotFavorited = isSpotFavoritedInProfile.value(profile.profile_id)

      if (spotFavorited) break
    }

    return spotFavorited
  })

  const isSpotFavoritedInProfile = computed(() => {
    return (profileId) => {
      const profile = userStore.getProfileById(profileId)
      let spotFavorited = null

      for (let favoriteSpot of profile.favorite_spots) {
        if (
          !favoriteSpot.spot_id &&
          favoriteSpot.lat === lat.value &&
          favoriteSpot.lon === lon.value
        ) {
          spotFavorited = [profileId, favoriteSpot.fav_spot_id]
          break
        }

        if (!spot.value) continue;

        if (
          favoriteSpot.spot_id &&
          spot.value.spot_id &&
          favoriteSpot.spot_id === spot.value.spot_id
        ) {
          spotFavorited = [profileId, favoriteSpot.fav_spot_id]
          break
        }
      }

      return spotFavorited
    }
  });

  const isContainerFavoritedInProfile = computed(() => {
    return (profileId) => {
      const profile = userStore.getProfileById(profileId)
      let containerFavorited = null

      for (let container of profile.containers) {

        if (container.container_id) {
          containerFavorited = [profileId, container.container_id]
          break
        }
      }

      return containerFavorited;
    }
  });

  const stationApiDataValues = computed(() => {
    const apiDataValues = defaultStation.value.data_values[0];
    const dataValues = {};
    
    spotRecordDetails.value.forEach((spotRecordDetail, i) => {
      const dataValue = scrubData(apiDataValues[i]);

      dataValues[spotRecordDetail] = dataValue;
    });

    return dataValues;
  });

  const formattedData = computed(() => ({
    windAvg: formatData(stationApiDataValues.value.avg, userStore.units.wind),
    windAvgRounded: formatData(stationApiDataValues.value.avg, userStore.units.wind, { round: true }),
    windlull: formatData(stationApiDataValues.value.lull, userStore.units.wind),
    windGust: formatData(stationApiDataValues.value.gust, userStore.units.wind),
    windDir: formatData(stationApiDataValues.value.dir, '&deg;', { useSpacer: false }),
    windDirText: formatData(stationApiDataValues.value.dir_text),
    airTemp: formatData(stationApiDataValues.value.atemp, `&deg;${userStore.units.temp}`),
    airPressure: formatAirPressure.value(stationApiDataValues.value.pres),
    surfaceTemp: formatData(stationApiDataValues.value.surface_temp, `&deg;${userStore.units.temp}`),
    waterSkinTemp: formatData(stationApiDataValues.value.water_skin_temp, `&deg;${userStore.units.temp}`),
    relativeHumidity: formatData(stationApiDataValues.value.humidity, '%', { useSpacer: false }),
    stationElevation: processStationElevationData(stationApiDataValues.value.station_elevation),
    waterTemp: formatData(stationApiDataValues.value.wtemp, `&deg;${userStore.units.temp}`),
    waveHeight: formatData(stationApiDataValues.value.wave_height, 'ft'),
    wavePeriod: formatData(stationApiDataValues.value.wave_period, 'ft'),
    waterLevel: formatData(stationApiDataValues.value.water_level, 'ft')
  }));

  const formatAirPressure = computed(() => {
    return (value) => {
      if (!value) return formatData(stationApiDataValues.value.pres, 'mb')

      return value < 500
        ? formatData(stationApiDataValues.value.pres, 'inHg')
        : formatData(stationApiDataValues.value.pres, 'mb')
    }
  }) 

  async function getSpotDetailById(spotId, settings = {}, overrideCache = false) {
    getSavedNCWindSettings(spotId);

    const data = await getSpotDetailSetByList([spotId], false, settings, overrideCache);

    lastAPICallFunction.value = [getSpotDetailById, [spotId], false, settings, overrideCache];

    if (data.error) {
      const message = !spotId ? 'Spot id was not provided.' : data.message;

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot id.`,
        message
      }         

      return data
    }

    updateSpotData(data);

    await getAvailableModelDataBySpotId(spotId);

    return data;
  }

  async function getSpotDetailByFavoriteId(favSpotId, settings = {}) {
    getSavedNCWindSettings(favSpotId);

    const data = await getSpotDetailSetByList([favSpotId], true, settings);

    lastAPICallFunction.value = [getSpotDetailSetByList, [favSpotId], true, settings];

    if (data.error) {
      const message = !spotId ? 'Spot id was not provided.' : data.message

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot id.`,
        message
      }      

      return data
    }

    updateSpotData(data)

    await getAvailableModelDataByFavoriteSpotId(favSpotId);

    return data;
  }

  async function getSpotDetailByLatLon(latLon, settings = {}) {
    const data = await getSpotDetailSetByList([latLon], false, settings)

    lastAPICallFunction.value = [getSpotDetailSetByList, [latLon], true, settings]

    if (data.error) {
      const message = !latLon ? 'Lat/lon was not provided.' : data.message

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot.`,
        message
      }         

      return data;
    }

    updateSpotData(data);

    const [lat, lon] = latLon.split(':');

    await getAvailableModelDataByLatLon(lat, lon);

    return data
  }  

  async function getSpotDetailSetByList(spotList, favorite = false, settings = {}, overrideCache = false) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      include_spot_products: true,
      stormprint_only: false,
      wf_token: sessionStore.token
    }

    if (!ncWindDataEnabled.value) {
      defaultSettings.nc_wind = false;
    }

    const params = Object.assign(defaultSettings, settings)

    if (favorite) {
      params.fav_spot_list = spotList.join(',')
    } else {
      params.spot_list = spotList.join(',')
    }

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotDetailSetByList?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url, {}, true, overrideCache)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      if (data.value.spot_count === 0) {
        throw 'No spots found'
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error, url }
    }
  }

  async function getNearbyTidesByLatLon(lat, lon) {
    const latLon = `${lat}:${lon}`;
    const data = await getSpotSetBySearch(latLon, { distance: 20, spot_types: '10,18' })

    if (data.error) return data

    nearbyTidesData.value = data;

    return data; 
  }

  async function getSpotSetBySearch(search, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      include_spot_products: false,
      stormprint_only: false,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    params.search = search;

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotSetBySearch?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getAvailableModelDataBySpotId(spotId, settings = {}) {
    const data = await getAvailableModelsBySpot(spotId, false, settings);

    if (data.error) return data;

    updateModelData(data);

    return data;
  }

  async function getAvailableModelDataByFavoriteSpotId(favSpotId, settings = {}) {
    const data = await getAvailableModelsBySpot(favSpotId, true, settings);

    if (data.error) return data;

    updateModelData(data);

    return data    
  }

  async function getAvailableModelDataByLatLon(lat, lon, settings = {}) {
    const data = await getAvailableModelsByLatLon(lat, lon, settings)

    if (data.error) return data;

    updateModelData(data);

    return data    
  }

  async function getAvailableModelsBySpot(id, favorite = false, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    if (favorite) {
      params.fav_spot_id = id;
    } else {
      params.spot_id = id;
    }

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/model/getAvailableModelsBySpot?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  } 

  async function getAvailableModelsByLatLon(lat, lon, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    params.lat = lat;
    params.lon = lon;

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/model/getAvailableModelsByLatLon?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  function getSavedNCWindSettings(id = spotId.value) {
    if ($WEB_VIEW) return ncWindDataEnabled.value;

    const ncWindLocalStorage = localStorage.getItem('ncWindDisabled') || '[]';

    ncWindDataEnabled.value = !ncWindLocalStorage.includes(id);
  }  

  function setSavedNCWindSettings(enabled) {
    let ncWindLocalStorage = JSON.parse(localStorage.getItem('ncWindDisabled')) || []

    if (!enabled) {
      ncWindLocalStorage.push(spotId.value)
    } else {
      ncWindLocalStorage = ncWindLocalStorage.filter((id) => id !== spotId.value)
    }

    localStorage.setItem('ncWindDisabled', JSON.stringify(ncWindLocalStorage))
  }  

  function setSavedNCWindSettingsWebView(value) {
    if (!$WEB_VIEW) return;

    if ($PLATFORM === 'android') {
      const params = ['nc_wind_data_enabled', value]

      alert(params.join(','))
    } else {
      const info = {
        type: 'nc_wind_data_enabled',
        value: value
      }

      window.webkit.messageHandlers.observe.postMessage(info)
    }
  }  

  function updateNCWindDataEnabled(enabled) {
    if ($WEB_VIEW) {
      setSavedNCWindSettingsWebView(enabled)
      return;
    }

    setSavedNCWindSettings(enabled);
  }

  function updateSpotData(data) {
    spots.value = data.spots;
    spot.value = data.spots[0];
    spotId.value = data.spots[0].spot_id;
    spotId.value = data.spots[0].spot_id;
    spotFavoriteId.value = data.spots[0].fav_spot_id
    spotName.value = data.spots[0].name;
    spotRecordDetails.value = data.spots[0].data_names
    spotProducts.value = data.spots[0].spot_product_ids;
    spotMessage.value = data.spots[0].spot_message;
    
    lat.value = data.spots[0].lat;
    lon.value = data.spots[0].lon;
    
    currentTimeLocal.value = data.current_time_local;
    currentTimeUTC.value = data.current_time_utc;
    localTimezone.value = data.local_timezone;
    localTimezoneShort.value = data.local_timezone_short;
    localUTCOffset.value = data.local_utc_offset;
    
    iconColor.value = data.spots[0].icon_color;
    iconType.value = data.spots[0].icon_type;
    
    upgradeAvailable.value = data.spots[0].upgrade_available;
    
    getDefaultStation(data.spots[0].stations);
  }

  function getDefaultStation(stations) {
    if (stations.length === 0) return;

    defaultStation.value = stations[0];

    // Multi-station spot. Use station with lowest elevation as default.
    if (stations.length > 1) {
      for (let i = 1; i < stations.length; i++) {
        if ( stations[i].station_elevation && defaultStation.value.station_elevation > stations[i].station_elevation ) {
          defaultStation.value = stations[i];
        }
      }
    }
  }

  function updateModelData(data) {
    forecastModelData.value = data;

    isModelUpgradeAvailable(data.model_summary);
  }

  function isModelUpgradeAvailable(modelData) {
    for (let model of modelData) {
      if (model.upgrade_available) {
        modelUpgradeAvailable.value = true;
        break;
      }
    }
  }

  function scrubData(value) {
    return (
      typeof value === undefined ||
      value === null ||
      value === 'null' ||
      value === '' ||
      value === 'undefined' ||
      value === undefined
    ) ? null : value
  }

  function formatData(data, unit, settings = {}) {
    if (data === null || status.value === 4) return '<span class="jwx-data-value__value">&ndash;&ndash;</span>'

    const defaultSettings = {
      useSpacer: false,
      spacer: '',
      round: false
    }

    settings = Object.assign({}, defaultSettings, settings)

    const spacer = settings.useSpacer ? settings.spacer : ''

    if (settings.round) data = Math.round(data)

    return unit
      ? `<span class="jwx-data-value__value">${data}</span>${spacer}<span class="jwx-data-value__unit">${unit}</span>`
      : `<span class="jwx-data-value__value">${data}</span>`
  }

  function processStationElevationData(value) {
    if (userStore.units.distance === 'ft') {
      return formatData(value, 'userStore.units.distance')
    }

    const convertedValue = unitConverter.convert('ft', 'm', value)
    return formatData(convertedValue, 'm')
  }

  watch(ncWindDataEnabled, (newValue) => {
    updateNCWindDataEnabled(newValue);

    if (!lastAPICallFunction.value) return;
    const apiCallfunction = lastAPICallFunction.value[0];
    const args = lastAPICallFunction.value.slice(1);

    apiCallfunction(...args);
  });


  return {
    spots,
    spot,
    spotId,
    spotFavoriteId,
    spotName,
    spotMessage,
    status,
    sourceMessage,
    statusMessage,
    spotProducts,
    stationApiDataValues,
    defaultStation,
    lat,
    lon,
    iconColor,
    iconType,
    currentTimeLocal,
    currentTimeUTC,
    localTimezone,
    localTimezoneShort,
    localUTCOffset,
    upgradeAvailable,
    modelUpgradeAvailable,
    ncWindDataEnabled,
    isNCWind,
    isNCWindCapable,
    isNowcast,
    isSpotFavorited,
    isSpotFavoritedInProfile,
    isContainerFavoritedInProfile,
    formattedData,
    forecastModels,
    forecastModelData,
    nearbyTidesData,
    hasNearbyTides,
    hasNearbyTidesOrCurrents,
    hasNearbyCurrents,
    hasSpotProduct,
    getSpotDetailById,
    getSpotDetailByFavoriteId,
    getSpotDetailByLatLon,
    getSpotDetailSetByList,
    getAvailableModelDataBySpotId,
    getAvailableModelDataByFavoriteSpotId,
    getAvailableModelDataByLatLon,
    getAvailableModelsBySpot,
    getNearbyTidesByLatLon,
    getSavedNCWindSettings,
    updateNCWindDataEnabled
  }
})
